/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import {
  Box,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  Flex,
  Select,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getSystemListApi } from 'api/auth.api';
import { createSystemAxisMapping } from 'api/auth.api';
import { getSystemAxisMappingById } from 'api/auth.api';
import { updateSystemAxisMapping } from 'api/auth.api';

function SystemAxisMappingForm({ editId, onSuccess }) {
  const id = editId?._id;
  const toast = useToast();
  const { data: systemData } = useQuery({
    queryKey: ['systems'],
    queryFn: getSystemListApi,
  });

  const axisList = systemData?.data?.result || [];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      diseaseEN: '',
      diseaseHN: '',
      systemId: '',
      axisCode: '',
    },
  });

  /* ================= GET BY ID ================= */
  const { data: editData, isLoading: isLoading2 } = useQuery({
    queryKey: ['systemAxisMappingById', id],
    queryFn: () => getSystemAxisMappingById(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (editData?.data?.result) {
      const d = editData.data.result;

      reset({
        diseaseEN: d.diseaseEN,
        diseaseHN: d.diseaseHN,
        systemId: d.systemId,
        axisCode: d.axisCode,
      });
    }
  }, [editData, reset]);

  /* ================= CREATE ================= */
  const createMutation = useMutation({
    mutationFn: createSystemAxisMapping,
    onSuccess: () => {
      toast({
        title: 'System axis created successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-center',
      });
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Failed to create system axis',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
      });
    },
  });

  /* ================= UPDATE ================= */
  const updateMutation = useMutation({
    mutationFn: updateSystemAxisMapping,
    onSuccess: () => {
      toast({
        title: 'System axis mapping updated successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-center',
      });
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Failed to update system axis',
        description: error?.response?.data?.message || error?.message,
        status: 'error',
      });
    },
  });

  /* ================= SUBMIT ================= */
  const submitHandler = (formData) => {
    if (id) {
      updateMutation.mutate({ id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Box w="100%" mt={6} display="flex" justifyContent="center">
      <Card w="100%" maxW="900px" rounded="xl" shadow="md">
        <CardBody>
          <form onSubmit={handleSubmit(submitHandler)}>
            {/* ROW 1 */}
            <Flex gap={4} mb={3}>
              <FormControl isInvalid={errors.diseaseEN} isRequired>
                <FormLabel>Disease Name</FormLabel>
                <Input
                  placeholder="Enter disease name"
                  {...register('diseaseEN', {
                    required: 'Disease Name is required',
                  })}
                />
                {errors.diseaseEN && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.diseaseEN?.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.diseaseHN} isRequired>
                <FormLabel>रोग का नाम</FormLabel>
                <Input
                  placeholder="Enter disease name hindi"
                  {...register('diseaseHN', {
                    required: 'Disease Name Hindi is required',
                  })}
                />
                {errors.diseaseHN && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.diseaseHN?.message}
                  </Text>
                )}
              </FormControl>
            </Flex>

            {/* ROW 2 */}
            <Flex gap={4} mb={3}>
              {/* Axis Name Select */}
              <FormControl isInvalid={errors.systemId} isRequired>
                <FormLabel>Axis Name</FormLabel>
                <Select
                  placeholder={isLoading2 ? 'Loading axes...' : 'Select axis'}
                  {...register('systemId', {
                    required: 'Axis Name is required',
                    onChange: (e) => {
                      const selectedAxis = axisList.find(
                        (axis) => axis._id === e.target.value,
                      );
                      setValue('axisCode', selectedAxis?.axisCode || '');
                    },
                  })}
                >
                  {axisList.map((axis) => (
                    <option key={axis._id} value={axis._id}>
                      {axis.name}
                    </option>
                  ))}
                </Select>

                {errors.systemId && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {' '}
                    {errors.systemId.message}{' '}
                  </Text>
                )}
              </FormControl>

              {/* Axis Code (auto-filled) */}
              <FormControl>
                <FormLabel>Axis Code</FormLabel>
                <Input
                  disabled
                  placeholder="Axis Code"
                  {...register('axisCode')}
                />
              </FormControl>
            </Flex>

            {/* SUBMIT */}
            <Flex justify="center" mt={4}>
              <Button
                type="submit"
                colorScheme="teal"
                isLoading={isLoading}
                w="160px"
              >
                {editId ? 'Update' : 'Submit'}
              </Button>
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
}

export default SystemAxisMappingForm;
